import axiosClient from "./axiosClient.js";

const shopApi = {
  getShops: async ({ accountId, staffId }) => {
    const url = `/shops/getShopsByStaffId?accountId=${accountId}&staffId=${staffId}`;
    return await axiosClient.post(url);
  },

  createShop: async (data) => {
    const url = `/shops/createOrUpdate`;
    return await axiosClient.post(url, data);
  },
  updateShop: async (shopId, data) => {
    const url = `/shops/${shopId}`;
    return await axiosClient.put(url, data);
  },

  relogin: async (data) => {
    const url = `/shops/relogin`;
    return await axiosClient.post(url, data);
  },

  checkLoginStatus: async (data) => {
    const url = `/shops/checkLoginStatus`;
    return await axiosClient.post(url, data);
  },

  waitLogin: async (shopId, profileId, oldQrImgSrc = "") => {
    const url = `/shops/waitLogin?shopId=${shopId}&profileId=${profileId}`;
    return await axiosClient.post(url, {
      oldQrImgSrc,
    });
  },

  logout: async (data) => {
    const url = `/shops/logout`;
    return await axiosClient.post(url, data);
  },

  delete: async (data) => {
    const url = `/shops/deleteShop`;
    return await axiosClient.post(url, data);
  },

  loadContacts: async (data) => {
    const url = `/shops/loadContacts`;
    return await axiosClient.post(url, data);
  },

  getContacts: async (
    shopId,
    isFirst = 0,
    page = 1,
    limit = 20,
    filters = {},
    signal,
  ) => {
    const url = `/shops/getContacts?shopId=${shopId}&isFirst=${isFirst}&page=${page}&limit=${limit}`;
    return await axiosClient.post(url, filters, signal ? { signal } : undefined);
  },
  searchContacts: async (
    shopId,
    query,
    limit = 50,
    offset = 0,
    signal,
    filters = {},
  ) => {
    return await axiosClient.post(
      `/search/contacts`,
      { shopId, query, limit, offset, filters },
      signal ? { signal } : undefined,
    );
  },
  searchMessages: async (
    shopId,
    query,
    limit = 50,
    offset = 0,
    signal,
    filters = {},
  ) => {
    return await axiosClient.post(
      `/search/messages`,
      { shopId, query, limit, offset, filters },
      signal ? { signal } : undefined,
    );
  },
  getMoreMessages: async (shopId, uid, page = 1, limit = 10) => {
    const url = `/shops/getMoreMessages?shopId=${shopId}&uid=${uid}&page=${page}&limit=${limit}`;
    return await axiosClient.get(url);
  },
  getMessagesByAnchor: async (shopId, uid, options = {}) => {
    const { anchorDate, direction = "before", limit = 20 } = options;
    const params = new URLSearchParams({
      shopId,
      uid,
      direction,
      limit: limit.toString(),
    });
    if (anchorDate) params.append("anchorDate", anchorDate);
    const url = `/shops/getMessagesByAnchor?${params.toString()}`;
    return await axiosClient.get(url);
  },
  findMessageByMsgId: async (shopId, uid, msgId) => {
    const url = `/shops/findMessageByMsgId?shopId=${shopId}&uid=${uid}&msgId=${msgId}`;
    return await axiosClient.get(url);
  },
  getContactsWithoutMessages: async (shopId) => {
    const url = `/shops/getContactsWithoutMessages?shopId=${shopId}`;
    return await axiosClient.post(url);
  },
  getContactByUid: async (shopId, uid) => {
    const url = `/shops/getContactByUid?shopId=${shopId}&uid=${uid}`;
    return await axiosClient.get(url);
  },
  getContactByUids: async (shopId, uids, isGetMessages = 0) => {
    const url = `/shops/getContactByUids`;
    return await axiosClient.post(url, {
      shopId,
      uids,
      isGetMessages: isGetMessages,
    });
  },
  sendMessage: async (data) => {
    const url = `/shops/sendMessage`;
    return await axiosClient.post(url, data);
  },
  shareMessage: async (data) => {
    const url = `/shops/shareMessage`;
    return await axiosClient.post(url, data);
  },
  getContactsFiltered: async (shopId, filters = {}) => {
    const url = `/shops/getContactsFiltered?shopId=${shopId}`;
    return await axiosClient.post(url, filters);
  },
  getFriendContacts: async (shopId) => {
    const url = `/shops/getFriendContacts?shopId=${shopId}`;
    return await axiosClient.get(url);
  },
  getFilterCounts: async (shopId, staffId, presets = []) => {
    const url = `/shops/getFilterCounts?shopId=${shopId}`;
    return await axiosClient.post(url, { staffId, presets });
  },
  getUnreadMessageCount: async (shopId, filters = {}) => {
    const url = `/shops/getUnreadMessageCount?shopId=${shopId}`;
    return await axiosClient.post(url, filters);
  },
  getTagZalos: async (shopId) => {
    const url = `/shops/getTagZalos?shopId=${shopId}`;
    return await axiosClient.post(url);
  },
  updateTagZalos: (shopId, data, actionParams = {}) => {
    const url = `/shops/updateTagZalos`;
    return axiosClient.post(url, { shopId, data, ...actionParams });
  },
  sendImage: async (data) => {
    const formData = new FormData();
    formData.append("file", data.file, data.file.name);

    const url = `/shops/sendImage`;

    const params = {
      shopId: data.shopId,
      uid: data.uid,
      isGroup: data.isGroup,
      width: data.width,
      height: data.height,
    };

    // Add grouped image params if provided
    if (data.groupLayoutId != null) {
      params.groupLayoutId = data.groupLayoutId;
      params.totalItemInGroup = data.totalItemInGroup;
      params.isGroupLayout = data.isGroupLayout;
      params.idInGroup = data.idInGroup;
    }

    return await axiosClient.post(url, formData, {
      params,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Send image(s) using zca-js (V2)
   * Simpler API - no need for browser-side Zalo session credentials
   * zca-js handles upload and metadata automatically
   * Supports multiple files in single request
   */
  sendImageV2: async (data) => {
    const formData = new FormData();

    // Support both single file and array of files
    const fileArray = Array.isArray(data.files)
      ? data.files
      : [data.files || data.file];
    fileArray.forEach((file, index) => {
      // Handle both File (has name) and Blob (no name) objects
      const fileName =
        file.name || `image_${index}.${file.type?.split("/")[1] || "jpg"}`;
      formData.append("files", file, fileName);
    });

    const url = `/shops/sendImageV2`;

    const params = {
      shopId: data.shopId,
      uid: data.uid,
      isGroup: data.isGroup,
      cliMsgId: data.cliMsgId,
    };

    return await axiosClient.post(url, formData, {
      params,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  searchContact: async (data) => {
    const url = `/shops/searchContact`;
    return await axiosClient.post(url, data);
  },
  updateProfileInfo: async (data) => {
    const url = `/shops/updateProfileInfo`;
    return await axiosClient.post(url, data);
  },
  lastOnline: async (data) => {
    const url = `/shops/lastOnline`;
    return await axiosClient.post(url, data);
  },
  createContacts: async (data) => {
    const url = `/shops/createContacts`;
    return await axiosClient.post(url, data);
  },
  updateContact: async (data) => {
    const url = `/shops/updateContact`;
    return await axiosClient.post(url, data);
  },
  addFriend: async (data) => {
    const url = `/shops/addFriend`;
    return await axiosClient.post(url, data);
  },
  getZaloUserInfo: async (data) => {
    const url = `/shops/getZaloUserInfo`;
    return await axiosClient.post(url, data);
  },
  removeFriend: async (data) => {
    const url = `/shops/removeFriend`;
    return await axiosClient.post(url, data);
  },
  acceptFriend: async (data) => {
    return await axiosClient.post(`/shops/acceptFriend`, data);
  },
  rejectFriend: async (data) => {
    return await axiosClient.post(`/shops/rejectFriend`, data);
  },
  withdrawFriend: async (data) => {
    return await axiosClient.post(`/shops/withdrawFriend`, data);
  },
  getFriendSuggestions: async (shopId) => {
    return await axiosClient.get(
      `/shops/getFriendSuggestions?shopId=${shopId}`,
    );
  },
  getReqStatus: async (data) => {
    const url = `/shops/getReqStatus`;
    return await axiosClient.post(url, data);
  },
  createGroup: async (data) => {
    const url = `/shops/createGroup`;
    return await axiosClient.post(url, data);
  },
  leaveGroup: async (data) => {
    const url = `/shops/leaveGroup`;
    return await axiosClient.post(url, data);
  },
  inviteGroup: async (data) => {
    const url = `/shops/inviteGroup`;
    return await axiosClient.post(url, data);
  },
  loadGroupMembers: async (data) => {
    const url = `/shops/loadGroupMembers`;
    return await axiosClient.post(url, data);
  },
  renameZalo: async (data) => {
    const url = `/shops/renameZalo`;
    return await axiosClient.post(url, data);
  },
  reactMessage: async (data) => {
    const url = `/shops/reactMessage`;
    return await axiosClient.post(url, data);
  },
  reactMessageGroup: async (data) => {
    const url = `/shops/reactMessageGroup`;
    return await axiosClient.post(url, data);
  },
  quote: async (data) => {
    const url = `/shops/quote`;
    return await axiosClient.post(url, data);
  },
  quoteGroup: async (data) => {
    const url = `/shops/quoteGroup`;
    return await axiosClient.post(url, data);
  },

  // Pinned Messages
  pinMessage: async (data) => {
    const url = `/shops/pinMessage`;
    return await axiosClient.post(url, data);
  },

  unpinMessage: async (data) => {
    const url = `/shops/unpinMessage`;
    return await axiosClient.post(url, data);
  },

  getPinnedMessages: async (shopId, conversationUid) => {
    const url = `/shops/getPinnedMessages?shopId=${shopId}&conversationUid=${conversationUid}`;
    return await axiosClient.get(url);
  },

  // Unread Message Count
  markConversationAsRead: async (shopId, uid, staffId) => {
    const url = `/shops/${shopId}/contacts/${uid}/mark-read`;
    return await axiosClient.put(url, { staffId });
  },

  // Pin Conversations
  pinConversation: async (shopId, uid, isGroup = false) => {
    const url = `/shops/pinConversation`;
    return await axiosClient.post(url, { shopId, uid, isGroup });
  },

  unpinConversation: async (shopId, uid, isGroup = false) => {
    const url = `/shops/unpinConversation`;
    return await axiosClient.post(url, { shopId, uid, isGroup });
  },

  // Hide Conversations
  hideConversation: async (shopId, uid) => {
    const url = `/shops/hideConversation`;
    return await axiosClient.post(url, { shopId, uid });
  },

  unhideConversation: async (shopId, uid) => {
    const url = `/shops/unhideConversation`;
    return await axiosClient.post(url, { shopId, uid });
  },

  copyData: async (data) => {
    const url = `/shops/copyData`;
    return await axiosClient.post(url, data);
  },

  // Update staff info on message when isSending matches server message
  updateMessageStaff: async (data) => {
    const url = `/shops/updateMessageStaff`;
    return await axiosClient.post(url, data);
  },
};

export default shopApi;
